import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { QuoteOrderSampleService } from 'src/app/admin-core/apiservices/quote-order-sample.service';
import { ChangeDetectorRef, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Constants } from '../../../../admin-core/constants';
import { AlertService, SpinnerService, } from '../../../../admin-core/services';
import { SubscribersService } from "@coreservices";

@Component({
  standalone: false,
  selector: 'app-upload-sample-packing-image-modal-new',
  // Eager: Angular 22 defaults every component to OnPush, which would leave this view
  // unpainted after its subscribe callbacks (see app.component.ts for the full note).
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './upload-sample-packing-image-modal-new.component.html',
  styleUrls: ['./upload-sample-packing-image-modal-new.component.css']
})
export class UploadSamplePackingImageModalNewComponent implements OnInit {
  @ViewChild('video') videoElement: ElementRef;
  @ViewChild('canvas') canvasElement: ElementRef;
  videoStream!: MediaStream;
  isFrontCamera: boolean = false;
  isPictureTaken: boolean = false;
  capturedImage!: string;
  btnLabel: string = 'Take Picture';
  isWebcamStarted: boolean = false
  @Input() productsRow: any;
  base64Regex = /^data:image\/([a-zA-Z]*);base64,([^\"]*)$/;
  isLoading: boolean = false;
  isPermissionAccessDenied: boolean = false;
  constructor(
    public ngbActiveModal: NgbActiveModal,
    public alertService: AlertService,
    private quoteOrderSampleService: QuoteOrderSampleService,
    public changeDetection: ChangeDetectorRef,
    private subscribersService: SubscribersService,
    public spinnerService: SpinnerService,
  ) {
  }


  /************************************
   * Life cycle hooks start
   ************************************/

  ngOnInit(): void {
  }

  ngOnDestroy(){
    this.stopWebcam()
  }

  /************************************
   * Life cycle hooks end
   ************************************/

    /************************************
   * Other functions start
   ************************************/

    async startWebcam(): Promise<void> {
      const constraints = {
        video: {
          facingMode: this.isFrontCamera ? 'user' : 'environment'
        }
      };

      try {
        this.isWebcamStarted = true;
        this.videoStream = await navigator?.mediaDevices?.getUserMedia(constraints);

        // Attach the stream to the video element
        if (this.videoElement && this.videoElement.nativeElement) {
          this.videoElement.nativeElement.srcObject = this.videoStream;
          this.videoElement.nativeElement.play();

          // Listen for when the stream becomes inactive
          this.videoStream.getTracks().forEach(track => {
            track.onended = () => {
              // Handle camera access denial
              this.handleCameraAccessDenied();
            };
          });

        } else {
          this.isWebcamStarted = false;
          this.stopWebcam();
          this.ngbActiveModal.close(true);
        }
        this.isPictureTaken = false;
      } catch (err) {
        this.handleCameraError(err);
      }
    }

    private handleCameraAccessDenied(): void {
      this.alertService.error(Constants.CAMERA_ACCESS_DENIED_ERROR_MESSAGE);
      this.isWebcamStarted = false;
      this.isPermissionAccessDenied = true;
      this.stopWebcam();
    }

    private handleCameraError(err: any): void {
      switch (err.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
          this.handleCameraAccessDenied();
          break;
        case 'NotFoundError':
        case 'DevicesNotFoundError':
          this.alertService.error(Constants.NO_CAMERA_ERROR_MESSAGE);
          this.isWebcamStarted = false;
          this.isPermissionAccessDenied = true;
          break;
        default:
          console.error('Camera error:', err);
          // Handle other errors as needed
          break;
      }
    }


  stopWebcam(): void {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = null;
      }
    }
  }

  takePicture(): void {
    if (!this.canvasElement || !this.canvasElement.nativeElement ||
        !this.videoElement || !this.videoElement.nativeElement) {
          this.alertService.error(Constants.NO_CAMERA_ERROR_MESSAGE);
        this.isPermissionAccessDenied = true
      return;
    }

    this.btnLabel = 'Re Capture the image';
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    if (this.videoElement.nativeElement.videoWidth && this.videoElement.nativeElement.videoHeight) {
      canvas.width = this.videoElement.nativeElement.videoWidth;
      canvas.height = this.videoElement.nativeElement.videoHeight;
      context.drawImage(this.videoElement.nativeElement, 0, 0, canvas.width, canvas.height);
      this.capturedImage = canvas.toDataURL('image/png');
      this.isPictureTaken = true;
      this.stopWebcam();
    } else {
      this.alertService.error(Constants.NO_CAMERA_ERROR_MESSAGE);
      this.isPermissionAccessDenied = true
    }
  }


  retakePicture(): void {
    this.isPictureTaken = false;
    this.startWebcam();
  }

  switchCamera(): void {
    this.isFrontCamera = !this.isFrontCamera;
    this.stopWebcam();
    this.startWebcam();
  }

  uploadSampleImage() {
    this.isLoading = true;
    this.spinnerService.show();

    if (!this.capturedImage) {
      this.isLoading = false;
      this.spinnerService.hide();
      return;
    }

    const json = {
      quote_product_id: this.productsRow.id,
      packing_image: this.capturedImage
    };

    this.quoteOrderSampleService.uploadSamplePackingImage(json).subscribe(
      (res) => {
        this.alertService.success(Constants.CAPTURE_IMAGE_UPLOAD_SUCCESS_MESSAGE);
        this.subscribersService.emitSampleImageDetails({
          quote_product_id: res.quote_product_id,
          id: res.id,
          original_file_name: res.original_file_name
        });
        this.isLoading = false;
        this.spinnerService.hide();
        this.ngbActiveModal.close(true);
        this.changeDetection.detectChanges();
      },
      () => {
        this.isLoading = false;
        this.spinnerService.hide()
      }
    );
  }

  displaySampleImage() {
    this.isLoading = true;
    this.spinnerService.show();

    if (this.productsRow?.sample_packing_image?.[0]?.original_file_name) {
      const imageUrl = this.productsRow.sample_packing_image[0].original_file_name;

      if (this.base64Regex.test(imageUrl)) {
        this.isLoading = false;
        this.spinnerService.hide();
        return imageUrl;
      } else {
        const getKitPath = Constants.S3_PRODUCT_IMAGES_IMAGE_KIT_URL;
        this.isLoading = false;
        this.spinnerService.hide();
        return getKitPath + '/UPLOAD_SAMPLES_PACKING_IMAGES/' + this.productsRow.sample_packing_image[0].original_file_name;
      }
    }

    this.isLoading = false;
    this.spinnerService.hide();
  }

    /************************************
   * Other functions start
   ************************************/

}
